import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { BusApiService } from 'src/app/services/api/bus-api.service';
import { OrderApiService } from 'src/app/services/api/order-api.service';
import { MessageService } from 'src/app/services/message.service';

@Component({
  selector: 'app-open-refund-panel',
  templateUrl: './open-refund-panel.component.html',
  styleUrls: ['./open-refund-panel.component.scss'],
})
export class OpenRefundPanelComponent implements OnChanges {
  @Input() order!: any;
  /** 已提交退款详情（查看退款详情时由父级传入） */
  @Input() detail: any = null;

  loading = false;

  /** 1=表单页  2=已提交工单页 */
  tab = 1;

  /** 'origin' = 原路退回  |  'wallet' = 退回钱包 */
  method: 'origin' | 'wallet' = 'origin';

  /** 退款理由 */
  reason = '';
  reasonError = false;

  /** 原路退回金额 */
  originAmount = 0;
  /** 退回钱包金额 */
  walletAmount = 0;

  /** 信息对话 */
  logs: any = [];

  /** 回复输入 */
  replyText = '';
  sending = false;

  constructor(
    private api: OrderApiService,
    private message: MessageService,
    private router: Router,
  ) {}

  /** 是否使用钱包支付（pay_score > 0）→ 只能退回钱包 */
  get payScoreUsed(): boolean {
    return Number(this.order?.pay_score) > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.detail) {
      this.applyDetail();
    } else {
      if (this.payScoreUsed) {
        this.method = 'wallet';
      }
      this.getRefundAmount();
    }
  }

  /** 用已提交的退款详情填充 tab=2 视图 */
  private applyDetail() {
    this.tab = 2;
    this.logs = this.detail.messages;
    this.method = this.detail.application.refund_method || '';
    this.currentAmount = this.detail.application.refund_amount || 0;
  }

  selectMethod(m: 'origin' | 'wallet') {
    if (m === 'origin' && this.payScoreUsed) {
      return;
    }
    this.method = m;
    this.getRefundAmount();
  }

  /** 当前选中方式对应的预计退款金额 */
  currentAmount = 0;
  getRefundAmount() {
    if (this.method === 'origin') {
      this.currentAmount = this.order.pay_price * 0.9;
    }
    if (this.method === 'wallet') {
      this.currentAmount = this.order.pay_price;
    }
  }

  onReasonChange() {
    this.reasonError = false;
  }

  /** 最后一条消息是否为客服/系统回复（非用户）→ 用户可输入回复 */
  get canReply(): boolean {
    const logs = this.logs || [];
    const last = logs[logs.length - 1];
    return !!last && last.sender_type !== 'user';
  }

  /** 发送回复 */
  async sendReply() {
    const text = (this.replyText || '').trim();
    if (!text || this.sending) return;
    try {
      this.sending = true;
      const r = await this.api.postRefundLog(this.detail.application.id, text);
      if (r?.status === 0) {
        this.logs.push({ sender_type: 'user', content: text });
        this.replyText = '';
      } else {
        this.message.warn('发送失败');
      }
    } catch (error) {
      this.message.warn('发送失败，请稍后重试');
    } finally {
      this.sending = false;
    }
  }

  /** 退款状态主文案 */
  get statusText(): string {
    switch (this.detail.application?.status) {
      case 'Pending':
        return '退款处理中';
      case 'Completed':
        return '退款已完成';
      case 'Rejected':
        return '退款已驳回';
      case 'Cancelled':
        return '退款已取消';
      default:
        return '';
    }
  }

  /** 退款状态标签 */
  get statusTag(): string {
    switch (this.detail?.application.status) {
      case 'Pending':
        return '待解决';
      case 'Completed':
        return '已完成';
      case 'Rejected':
        return '已驳回';
      case 'Cancelled':
        return '已取消';
      default:
        return '';
    }
  }

  /** 退款状态色调（用于状态图标/标签变色） */
  get statusTone(): string {
    switch (this.detail?.application?.status) {
      case 'Completed':
        return 'completed';
      case 'Pending':
      default:
        return 'pending';
    }
  }

  /** 表单提交 */
  async submitRefund() {
    if (!this.reason.trim()) {
      this.reasonError = true;
      return;
    }

    try {
      this.loading = true;
      const r = await this.api.postRefund(
        this.order.id,
        this.method,
        this.reason,
      );
      if (r.status === 0) {
        this.router.navigate(['/user/profile/orders/refund'], {
          queryParams: { id: r.data.order_id, view: 'detail' },
        });
      } else {
        this.message.warn('提交失败');
      }
    } catch (error) {
      this.message.warn('提交失败，请稍后重试');
    } finally {
      this.loading = false;
    }
  }

  goBack() {
    this.router.navigate(['/user/profile/orders']);
  }
}
